/**
 * Vendor Bot Shop - Order Detail Page
 * Single order view with timeline, tracking, and digital delivery
 */

import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BottomNavigation } from '../../components/customer';
import { useTranslation } from '../../hooks/useTranslation';

interface OrderItem {
  id: number;
  product_id: number;
  name: string;
  price: number;
  quantity: number;
  product_type: 'physical' | 'downloadable' | 'keys' | 'subscription';
  image_url: string | null;
  // Digital delivery fields
  download_url?: string;
  download_token?: string;
  product_key?: string;
  key_revealed?: boolean;
}

interface ShippingAddress {
  full_name: string;
  phone: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface OrderTimeline {
  status: string;
  label: string;
  timestamp: string | null;
  completed: boolean;
}

interface PaymentInfo {
  method: string;
  method_name: string;
  status: string;
  payment_address?: string;
  payment_amount?: string;
  payment_currency?: string;
  redirect_url?: string;
}

interface Order {
  id: number;
  order_number: string;
  status: string;
  payment_status: string;
  subtotal: number;
  shipping_cost: number;
  discount_amount: number;
  tax_amount: number;
  total: number;
  coupon_code: string | null;
  created_at: string;
  updated_at: string;
  note: string | null;
  items: OrderItem[];
  shipping_address: ShippingAddress | null;
  shipping_method: string | null;
  tracking_number: string | null;
  tracking_carrier: string | null;
  timeline: OrderTimeline[];
  payment: PaymentInfo | null;
}

interface StoreInfo {
  store_name: string;
  currency: string;
  currency_symbol: string;
}

const OrderDetail: React.FC = () => {
  const { botUsername, orderId } = useParams<{ botUsername: string; orderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [order, setOrder] = useState<Order | null>(null);
  const [storeInfo, setStoreInfo] = useState<StoreInfo>({
    store_name: 'Store',
    currency: 'USD',
    currency_symbol: '$'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [revealedKeys, setRevealedKeys] = useState<Set<number>>(new Set());
  const [copiedKey, setCopiedKey] = useState<number | null>(null);
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);

  const baseUrl = process.env.REACT_APP_API_URL || '';
  const showPaymentPending = searchParams.get('payment') === 'pending';

  // Load order data
  const loadOrder = useCallback(async () => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const [storeRes, orderRes] = await Promise.all([
        fetch(`${baseUrl}/api/shop/${botUsername}/store-info`),
        fetch(`${baseUrl}/api/orders/${botUsername}/${orderId}`, { headers })
      ]);

      const [storeData, orderData] = await Promise.all([
        storeRes.json(),
        orderRes.json()
      ]);

      if (storeData.success) setStoreInfo(storeData.data);
      if (orderData.success) {
        // Map backend response to frontend expected format
        const data = orderData.data;

        // Safely parse shipping address
        let shippingAddress = null;
        if (data.shipping_address) {
          try {
            shippingAddress = typeof data.shipping_address === 'string'
              ? JSON.parse(data.shipping_address)
              : data.shipping_address;
          } catch (e) {
            console.warn('Failed to parse shipping address:', e);
            // Fallback: treat as plain text address
            shippingAddress = {
              full_name: '',
              address_line1: data.shipping_address,
              city: '',
              state: '',
              postal_code: '',
              country: '',
              phone: ''
            };
          }
        }

        const mappedOrder: Order = {
          id: data.id,
          order_number: String(data.order_number),
          status: data.status,
          payment_status: data.payment_status === 'paid' ? 'completed' : data.payment_status,
          subtotal: parseFloat(data.subtotal) || 0,
          shipping_cost: parseFloat(data.shipping_fee) || 0,
          discount_amount: parseFloat(data.discount_amount) || 0,
          tax_amount: parseFloat(data.tax_amount) || 0,
          total: parseFloat(data.total_amount) || 0,
          coupon_code: data.coupon_code || null,
          created_at: data.created_at,
          updated_at: data.updated_at || data.created_at,
          note: data.notes,
          shipping_address: shippingAddress,
          shipping_method: data.shipping_method || null,
          tracking_number: data.tracking_number,
          tracking_carrier: null,
          timeline: generateTimeline(data.status, data.created_at, data.paid_at, data.shipped_at, data.delivered_at),
          payment: null,
          items: (data.items || []).map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            name: item.product_name,
            price: parseFloat(item.unit_price) || 0,
            quantity: item.quantity,
            product_type: item.product_type || 'physical',
            image_url: item.product_image || null,
            download_url: item.download_link,
            download_token: item.download_link ? String(data.id) : undefined,
            product_key: item.product_keys,
            key_revealed: true
          }))
        };
        setOrder(mappedOrder);
      }
    } catch (error) {
      console.error('Error loading order:', error);
    } finally {
      setIsLoading(false);
    }
  }, [botUsername, orderId, baseUrl]);

  // Generate timeline based on order status
  const generateTimeline = (status: string, createdAt: string, paidAt: string | null, shippedAt: string | null, deliveredAt: string | null): OrderTimeline[] => {
    const timeline: OrderTimeline[] = [
      { status: 'pending', label: 'Order Placed', timestamp: createdAt, completed: true },
      { status: 'paid', label: 'Payment Confirmed', timestamp: paidAt, completed: ['paid', 'processing', 'shipped', 'delivered', 'completed'].includes(status) },
      { status: 'processing', label: 'Processing', timestamp: null, completed: ['processing', 'shipped', 'delivered', 'completed'].includes(status) },
      { status: 'shipped', label: 'Shipped', timestamp: shippedAt, completed: ['shipped', 'delivered', 'completed'].includes(status) },
      { status: 'delivered', label: 'Delivered', timestamp: deliveredAt, completed: ['delivered', 'completed'].includes(status) }
    ];
    return timeline;
  };

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  // Initialize Telegram WebApp
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();

      if (tg.themeParams?.bg_color) {
        tg.setBackgroundColor(tg.themeParams.bg_color);
        tg.setHeaderColor(tg.themeParams.bg_color);
      }
    }
  }, []);

  // Format price
  const formatPrice = (value: number) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status display
  const getStatusDisplay = (status: string) => {
    const statusMap: Record<string, { label: string; color: string; icon: string }> = {
      pending: { label: 'Pending', color: '#FF9800', icon: '⏳' },
      processing: { label: 'Processing', color: '#2196F3', icon: '⚙️' },
      shipped: { label: 'Shipped', color: '#9C27B0', icon: '📦' },
      delivered: { label: 'Delivered', color: '#4CAF50', icon: '✅' },
      completed: { label: 'Completed', color: '#4CAF50', icon: '✅' },
      cancelled: { label: 'Cancelled', color: '#f44336', icon: '❌' },
      refunded: { label: 'Refunded', color: '#757575', icon: '↩️' }
    };
    return statusMap[status] || { label: status, color: '#757575', icon: '📋' };
  };

  // Get payment status display
  const getPaymentStatusDisplay = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      pending: { label: 'Awaiting Payment', color: '#FF9800' },
      processing: { label: 'Processing', color: '#2196F3' },
      completed: { label: 'Paid', color: '#4CAF50' },
      failed: { label: 'Failed', color: '#f44336' },
      refunded: { label: 'Refunded', color: '#757575' }
    };
    return statusMap[status] || { label: status, color: '#757575' };
  };

  // Handle key reveal
  const handleRevealKey = (itemId: number) => {
    setRevealedKeys(prev => new Set(prev).add(itemId));
  };

  // Handle copy key
  const handleCopyKey = async (key: string, itemId: number) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(itemId);
      setTimeout(() => setCopiedKey(null), 2000);

      const tg = window.Telegram?.WebApp;
      tg?.HapticFeedback?.notificationOccurred('success');
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Handle download - sends file to user's Telegram chat
  const handleDownload = async (item: OrderItem) => {
    const tg = window.Telegram?.WebApp;

    if (!item.download_token) return;

    const token = localStorage.getItem('auth_token');

    try {
      // Send file directly to user's Telegram chat
      const response = await fetch(`${baseUrl}/api/downloads/${botUsername}/send-to-chat/${item.download_token}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        tg?.showAlert?.(t('downloads.sentToChat') || 'File sent to your Telegram chat!');
        tg?.HapticFeedback?.notificationOccurred('success');
      } else {
        throw new Error(data.error || 'Failed to send file');
      }
    } catch (error: any) {
      console.error('Download error:', error);
      tg?.showAlert?.(error.message || t('downloads.downloadFailed') || 'Failed to send file');
      tg?.HapticFeedback?.notificationOccurred('error');
    }
  };

  // Handle refresh payment status
  const handleRefreshPaymentStatus = async () => {
    if (isRefreshingStatus) return;

    setIsRefreshingStatus(true);
    const tg = window.Telegram?.WebApp;

    try {
      await loadOrder();

      // Check if status changed after reload
      if (order?.payment_status === 'pending') {
        tg?.showAlert?.(t('payment.noStatusChange'));
        tg?.HapticFeedback?.notificationOccurred('warning');
      } else {
        tg?.showAlert?.(t('payment.statusUpdated'));
        tg?.HapticFeedback?.notificationOccurred('success');
      }
    } catch (error) {
      console.error('Error refreshing status:', error);
    } finally {
      setIsRefreshingStatus(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Header>
          <BackButton onClick={() => navigate(`/shop/${botUsername}/dashboard?tab=orders`)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
          </BackButton>
          <HeaderTitle>{t('orders.orderDetails')}</HeaderTitle>
        </Header>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
        <BottomNavigation />
      </PageContainer>
    );
  }

  if (!order) {
    return (
      <PageContainer>
        <Header>
          <BackButton onClick={() => navigate(`/shop/${botUsername}/dashboard?tab=orders`)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
          </BackButton>
          <HeaderTitle>{t('orders.orderDetails')}</HeaderTitle>
        </Header>
        <Content>
          <EmptyState>
            <p>{t('orders.orderNotFound')}</p>
          </EmptyState>
        </Content>
        <BottomNavigation />
      </PageContainer>
    );
  }

  const statusDisplay = getStatusDisplay(order.status);
  const paymentDisplay = getPaymentStatusDisplay(order.payment_status);
  const hasPhysicalItems = order.items.some(item => item.product_type === 'physical');

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={() => navigate(`/shop/${botUsername}/dashboard?tab=orders`)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
        </BackButton>
        <HeaderTitle>{t('orders.orderNumber')}{order.order_number}</HeaderTitle>
      </Header>

      <Content>
        {/* Status Card */}
        <StatusCard $color={statusDisplay.color}>
          <StatusIcon>{statusDisplay.icon}</StatusIcon>
          <StatusInfo>
            <StatusLabel>{statusDisplay.label}</StatusLabel>
            <StatusDate>{formatDate(order.created_at)}</StatusDate>
          </StatusInfo>
          <PaymentBadge $color={paymentDisplay.color}>
            {paymentDisplay.label}
          </PaymentBadge>
        </StatusCard>

        {/* Refresh Payment Status Button for pending payments */}
        {order.payment_status === 'pending' && (
          <RefreshStatusButton
            onClick={handleRefreshPaymentStatus}
            disabled={isRefreshingStatus}
            $isRefreshing={isRefreshingStatus}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            {isRefreshingStatus ? t('payment.refreshing') : t('payment.refreshStatus')}
          </RefreshStatusButton>
        )}

        {/* Payment Pending Info */}
        {showPaymentPending && order.payment && order.payment.status === 'pending' && (
          <PaymentPendingCard>
            <SectionTitle>{t('orders.completePayment')}</SectionTitle>
            {order.payment.payment_address && (
              <>
                <PaymentLabel>{t('orders.sendTo', { amount: order.payment.payment_amount || '', currency: order.payment.payment_currency || '' })}</PaymentLabel>
                <PaymentAddress>
                  <AddressText>{order.payment.payment_address}</AddressText>
                  <CopyButton onClick={() => navigator.clipboard.writeText(order.payment!.payment_address!)}>
                    {t('common.copy')}
                  </CopyButton>
                </PaymentAddress>
                <PaymentNote>
                  {t('orders.paymentAutoConfirm')}
                </PaymentNote>
              </>
            )}
            {order.payment.redirect_url && (
              <PayButton onClick={() => window.location.href = order.payment!.redirect_url!}>
                {t('orders.completePayment')}
              </PayButton>
            )}
          </PaymentPendingCard>
        )}

        {/* Timeline */}
        {order.timeline && order.timeline.length > 0 && (
          <Section>
            <SectionTitle>{t('orders.timeline')}</SectionTitle>
            <Timeline>
              {order.timeline.map((step, index) => (
                <TimelineItem key={step.status} $completed={step.completed}>
                  <TimelineDot $completed={step.completed} />
                  {index < order.timeline.length - 1 && (
                    <TimelineLine $completed={order.timeline[index + 1].completed} />
                  )}
                  <TimelineContent>
                    <TimelineLabel>{step.label}</TimelineLabel>
                    {step.timestamp && (
                      <TimelineDate>{formatDate(step.timestamp)}</TimelineDate>
                    )}
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Section>
        )}

        {/* Tracking Info (for physical products) */}
        {hasPhysicalItems && order.tracking_number && (
          <Section>
            <SectionTitle>{t('orders.tracking')}</SectionTitle>
            <TrackingCard>
              <TrackingLabel>
                {order.tracking_carrier || 'Carrier'}: {order.tracking_number}
              </TrackingLabel>
              <TrackButton onClick={() => {
                // Could open tracking URL based on carrier
                const tg = window.Telegram?.WebApp;
                tg?.showAlert?.(`Tracking: ${order.tracking_number}`);
              }}>
                {t('orders.track')}
              </TrackButton>
            </TrackingCard>
          </Section>
        )}

        {/* Order Items */}
        <Section>
          <SectionTitle>{t('checkout.orderItems')}</SectionTitle>
          <ItemsList>
            {order.items.map(item => (
              <OrderItemCard key={item.id}>
                <ItemTop>
                  <ItemImageContainer>
                    {item.image_url ? (
                      <ItemImage src={item.image_url} alt={item.name} />
                    ) : (
                      <PlaceholderImage>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86l-3 3.87L9 13.14 6 17h12l-3.86-5.14z"/>
                        </svg>
                      </PlaceholderImage>
                    )}
                    <ProductTypeBadge $type={item.product_type}>
                      {item.product_type === 'physical' ? t('shop.productTypes.physical') :
                       item.product_type === 'downloadable' ? t('shop.productTypes.downloadable') :
                       item.product_type === 'keys' ? t('shop.productTypes.keys') : t('shop.productTypes.subscription')}
                    </ProductTypeBadge>
                  </ItemImageContainer>
                  <ItemInfo>
                    <ItemName>{item.name}</ItemName>
                    <ItemMeta>{t('cart.quantity')}: {item.quantity}</ItemMeta>
                  </ItemInfo>
                  <ItemPrice>
                    {storeInfo.currency_symbol}{formatPrice(item.price * item.quantity)}
                  </ItemPrice>
                </ItemTop>

                {/* Digital Delivery Actions */}
                {order.payment_status === 'completed' && (
                  <>
                    {/* Download Button */}
                    {item.product_type === 'downloadable' && item.download_token && (
                      <DigitalAction>
                        <DownloadButton onClick={() => handleDownload(item)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                          </svg>
                          {t('downloads.download')}
                        </DownloadButton>
                      </DigitalAction>
                    )}

                    {/* Product Key */}
                    {item.product_type === 'keys' && item.product_key && (
                      <DigitalAction>
                        {revealedKeys.has(item.id) || item.key_revealed ? (
                          <KeyContainer>
                            <KeyText>{item.product_key}</KeyText>
                            <CopyKeyButton
                              onClick={() => handleCopyKey(item.product_key!, item.id)}
                              $copied={copiedKey === item.id}
                            >
                              {copiedKey === item.id ? t('keys.copied') : t('keys.copy')}
                            </CopyKeyButton>
                          </KeyContainer>
                        ) : (
                          <RevealButton onClick={() => handleRevealKey(item.id)}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                            </svg>
                            {t('keys.revealKey')}
                          </RevealButton>
                        )}
                      </DigitalAction>
                    )}

                    {/* Subscription Access */}
                    {item.product_type === 'subscription' && (
                      <DigitalAction>
                        <AccessButton onClick={() => navigate(`/shop/${botUsername}/dashboard?tab=subscriptions`)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                          </svg>
                          {t('orders.viewSubscription')}
                        </AccessButton>
                      </DigitalAction>
                    )}
                  </>
                )}
              </OrderItemCard>
            ))}
          </ItemsList>
        </Section>

        {/* Shipping Info (for physical products) */}
        {hasPhysicalItems && (order.shipping_address || order.shipping_method) && (
          <Section>
            <SectionTitle>{t('checkout.shippingInfo')}</SectionTitle>

            {/* Shipping Method */}
            {order.shipping_method && (
              <ShippingMethodCard>
                <ShippingMethodIcon>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                  </svg>
                </ShippingMethodIcon>
                <ShippingMethodText>
                  <ShippingMethodLabel>{t('checkout.shippingMethod')}</ShippingMethodLabel>
                  <ShippingMethodValue>{order.shipping_method}</ShippingMethodValue>
                </ShippingMethodText>
              </ShippingMethodCard>
            )}

            {/* Shipping Address */}
            {order.shipping_address && (
              <AddressCard>
                <AddressLabel>{t('checkout.deliveryAddress')}</AddressLabel>
                <AddressLine><strong>{order.shipping_address.full_name}</strong></AddressLine>
                <AddressLine>
                  {order.shipping_address.address_line1}
                  {order.shipping_address.address_line2 && `, ${order.shipping_address.address_line2}`}
                </AddressLine>
                <AddressLine>
                  {order.shipping_address.city}{order.shipping_address.city && order.shipping_address.state ? ', ' : ''}{order.shipping_address.state} {order.shipping_address.postal_code}
                </AddressLine>
                {order.shipping_address.country && <AddressLine>{order.shipping_address.country}</AddressLine>}
                {order.shipping_address.phone && <AddressLine $muted>{order.shipping_address.phone}</AddressLine>}
              </AddressCard>
            )}
          </Section>
        )}

        {/* Order Summary */}
        <Section>
          <SectionTitle>{t('common.orderSummary')}</SectionTitle>
          <SummaryCard>
            <SummaryRow>
              <span>{t('cart.subtotal')}</span>
              <span>{storeInfo.currency_symbol}{formatPrice(order.subtotal)}</span>
            </SummaryRow>
            {order.shipping_cost > 0 && (
              <SummaryRow>
                <span>{t('checkout.shipping')}</span>
                <span>{storeInfo.currency_symbol}{formatPrice(order.shipping_cost)}</span>
              </SummaryRow>
            )}
            {order.tax_amount > 0 && (
              <SummaryRow>
                <span>{t('checkout.tax')}</span>
                <span>{storeInfo.currency_symbol}{formatPrice(order.tax_amount)}</span>
              </SummaryRow>
            )}
            {order.discount_amount > 0 && (
              <DiscountRow>
                <span>
                  {t('checkout.discount')}
                  {order.coupon_code && <CouponBadge>{order.coupon_code}</CouponBadge>}
                </span>
                <span>-{storeInfo.currency_symbol}{formatPrice(order.discount_amount)}</span>
              </DiscountRow>
            )}
            <TotalRow>
              <span>{t('cart.total')}</span>
              <span>{storeInfo.currency_symbol}{formatPrice(order.total)}</span>
            </TotalRow>
          </SummaryCard>
        </Section>

        {/* Order Note */}
        {order.note && (
          <Section>
            <SectionTitle>{t('checkout.orderNote')}</SectionTitle>
            <NoteCard>{order.note}</NoteCard>
          </Section>
        )}

        {/* Help Button */}
        <HelpButton onClick={() => {
          const tg = window.Telegram?.WebApp;
          tg?.showAlert?.(t('orders.helpMessage'));
        }}>
          {t('orders.needHelp')}
        </HelpButton>
      </Content>

      <BottomNavigation />
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  padding-bottom: calc(70px + env(safe-area-inset-bottom, 0px));
  max-width: 768px;
  margin: 0 auto;

  @media (min-width: 768px) {
    max-width: 900px;
  }
`;

const Header = styled.header`
  background: var(--tg-theme-bg-color, #ffffff);
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid var(--tg-theme-hint-color, #e0e0e0)20;

  @media (min-width: 375px) {
    padding: 16px;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--tg-theme-text-color, #000000);
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.h1`
  font-size: 15px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;

  @media (min-width: 375px) {
    font-size: 18px;
  }
`;

const Content = styled.main`
  padding: 12px;

  @media (min-width: 375px) {
    padding: 16px;
  }

  @media (min-width: 600px) {
    padding: 20px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--tg-theme-hint-color, #e0e0e0);
  border-top-color: var(--tg-theme-button-color, #3390ec);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--tg-theme-hint-color, #999);
`;

// Status Card
const StatusCard = styled.div<{ $color: string }>`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 10px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  border-left: 4px solid ${props => props.$color};

  @media (min-width: 375px) {
    border-radius: 12px;
    padding: 16px;
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const StatusIcon = styled.span`
  font-size: 28px;

  @media (min-width: 375px) {
    font-size: 32px;
  }
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusLabel = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;
`;

const StatusDate = styled.p`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  margin: 4px 0 0 0;
`;

const PaymentBadge = styled.span<{ $color: string }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$color}20;
  color: ${props => props.$color};
`;

// Payment Pending
const PaymentPendingCard = styled.div`
  background: #FF980010;
  border: 1px solid #FF9800;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
`;

const PaymentLabel = styled.p`
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  margin: 8px 0;
`;

const PaymentAddress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--tg-theme-bg-color, #ffffff);
  padding: 12px;
  border-radius: 8px;
  margin: 8px 0;
`;

const AddressText = styled.code`
  flex: 1;
  font-size: 12px;
  color: var(--tg-theme-text-color, #000000);
  word-break: break-all;
`;

const CopyButton = styled.button`
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
`;

const PaymentNote = styled.p`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  margin: 8px 0 0 0;
`;

const PayButton = styled.button`
  width: 100%;
  background: #FF9800;
  color: white;
  border: none;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 12px;
`;

// Section
const Section = styled.div`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;

  @media (min-width: 375px) {
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0 0 10px 0;

  @media (min-width: 375px) {
    font-size: 15px;
    margin: 0 0 12px 0;
  }
`;

// Timeline
const Timeline = styled.div`
  display: flex;
  flex-direction: column;
`;

const TimelineItem = styled.div<{ $completed: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  padding-bottom: 16px;

  &:last-child {
    padding-bottom: 0;
  }
`;

const TimelineDot = styled.div<{ $completed: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.$completed ? '#4CAF50' : 'var(--tg-theme-hint-color, #e0e0e0)'};
  flex-shrink: 0;
  margin-top: 4px;
`;

const TimelineLine = styled.div<{ $completed: boolean }>`
  position: absolute;
  left: 5px;
  top: 20px;
  width: 2px;
  height: calc(100% - 8px);
  background: ${props => props.$completed ? '#4CAF50' : 'var(--tg-theme-hint-color, #e0e0e0)'};
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineLabel = styled.p`
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;
`;

const TimelineDate = styled.p`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  margin: 2px 0 0 0;
`;

// Tracking
const TrackingCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  padding: 12px;
  border-radius: 8px;
`;

const TrackingLabel = styled.span`
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
`;

const TrackButton = styled.button`
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  border: none;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
`;

// Order Items
const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OrderItemCard = styled.div`
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  border-radius: 10px;
  padding: 12px;
`;

const ItemTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ItemImageContainer = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const PlaceholderImage = styled.div`
  width: 100%;
  height: 100%;
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tg-theme-hint-color, #999);
`;

const ProductTypeBadge = styled.span<{ $type: string }>`
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 8px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  background: ${props => {
    switch (props.$type) {
      case 'physical': return '#4CAF50';
      case 'downloadable': return '#2196F3';
      case 'keys': return '#9C27B0';
      case 'subscription': return '#FF9800';
      default: return '#757575';
    }
  }};
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemMeta = styled.span`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
`;

const ItemPrice = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
`;

// Digital Delivery Actions
const DigitalAction = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--tg-theme-hint-color, #e0e0e0)40;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  background: #2196F3;
  color: white;
  border: none;
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
`;

const RevealButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  background: #9C27B0;
  color: white;
  border: none;
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
`;

const KeyContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--tg-theme-bg-color, #ffffff);
  padding: 10px;
  border-radius: 8px;
`;

const KeyText = styled.code`
  flex: 1;
  font-size: 13px;
  color: var(--tg-theme-text-color, #000000);
  word-break: break-all;
`;

const CopyKeyButton = styled.button<{ $copied: boolean }>`
  background: ${props => props.$copied ? '#4CAF50' : '#9C27B0'};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
`;

const AccessButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  background: #FF9800;
  color: white;
  border: none;
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
`;

// Shipping Method
const ShippingMethodCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const ShippingMethodIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--tg-theme-button-color, #3390ec)15;
  color: var(--tg-theme-button-color, #3390ec);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ShippingMethodText = styled.div`
  flex: 1;
`;

const ShippingMethodLabel = styled.p`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  margin: 0 0 2px 0;
`;

const ShippingMethodValue = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: var(--tg-theme-text-color, #000000);
  margin: 0;
`;

// Address
const AddressCard = styled.div`
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  padding: 12px;
  border-radius: 8px;
`;

const AddressLabel = styled.p`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  margin: 0 0 8px 0;
`;

const AddressLine = styled.p<{ $muted?: boolean }>`
  font-size: 14px;
  color: ${props => props.$muted ? 'var(--tg-theme-hint-color, #999)' : 'var(--tg-theme-text-color, #000000)'};
  margin: 2px 0;
  line-height: 1.4;
`;

// Summary
const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: var(--tg-theme-hint-color, #999);
  padding: 4px 0;
`;

const DiscountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #4CAF50;
  padding: 4px 0;

  span:first-child {
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const CouponBadge = styled.span`
  background: #4CAF5015;
  color: #4CAF50;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  padding-top: 12px;
  margin-top: 8px;
  border-top: 1px solid var(--tg-theme-hint-color, #e0e0e0)40;
`;

// Note
const NoteCard = styled.div`
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  font-style: italic;
`;

// Help
const HelpButton = styled.button`
  width: 100%;
  background: transparent;
  color: var(--tg-theme-link-color, #3390ec);
  border: 1px solid var(--tg-theme-link-color, #3390ec);
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 16px;
`;

// Refresh Status Button
const RefreshStatusButton = styled.button<{ $isRefreshing?: boolean }>`
  width: 100%;
  padding: 12px;
  background: #FF9800;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
  opacity: ${props => props.$isRefreshing ? 0.7 : 1};

  &:disabled {
    cursor: not-allowed;
  }

  svg {
    animation: ${props => props.$isRefreshing ? 'spin 1s linear infinite' : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export default OrderDetail;
